import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useCompany } from "./useCompany";

export interface Loan {
  id: string;
  company_id: string;
  vehicle_id: string | null;
  lender_name: string;
  original_amount: number;
  interest_rate: number;
  term_months: number;
  origination_date: string;
  monthly_payment: number;
  status: string;
  expense_type: string | null;
  paid_by: string | null;
  paid_to: string | null;
  created_at: string;
  updated_at: string;
}

export interface LoanFormData {
  vehicle_id?: string | null;
  lender_name: string;
  original_amount: number;
  interest_rate: number;
  term_months: number;
  origination_date: string;
  monthly_payment: number;
  expense_type?: string | null;
  paid_by?: string | null;
  paid_to?: string | null;
}

export const useLoans = () => {
  const queryClient = useQueryClient();
  const { company } = useCompany();

  const { data: loans = [], isLoading } = useQuery({
    queryKey: ["loans", company?.id],
    enabled: !!company?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("loans")
        .select("*")
        .eq("company_id", company!.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Loan[];
    },
  });

  const addLoan = useMutation({
    mutationFn: async (loanData: LoanFormData) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      if (!company?.id) throw new Error("No company found");

      // 1. Create the loan
      const { data: loan, error } = await supabase
        .from("loans")
        .insert({
          ...loanData,
          company_id: company.id,
        })
        .select()
        .single();

      if (error) throw error;

      // 2. Calculate interest and principal splits from monthly payment
      const monthlyRate = loanData.interest_rate / 100 / 12;
      const interestAmount = Math.round(loanData.original_amount * monthlyRate * 100) / 100;
      const principalAmount = Math.round((loanData.monthly_payment - interestAmount) * 100) / 100;

      // 3. Spawn two recurring_expenses templates
      const startDate = loanData.origination_date;

      const templates = [
        {
          company_id: company.id,
          user_id: user.id,
          vehicle_id: loanData.vehicle_id || null,
          loan_id: loan.id,
          amount: interestAmount,
          loan_payment_type: "Interest",
          description: `${loanData.lender_name} — Interest`,
          frequency: "monthly",
          start_date: startDate,
          next_generation_date: startDate,
          is_active: true,
          expense_type: loanData.expense_type || null,
          paid_by: loanData.paid_by || null,
          paid_to: loanData.paid_to || null,
        },
        {
          company_id: company.id,
          user_id: user.id,
          vehicle_id: loanData.vehicle_id || null,
          loan_id: loan.id,
          amount: principalAmount,
          loan_payment_type: "Principal Paydown",
          description: `${loanData.lender_name} — Principal`,
          frequency: "monthly",
          start_date: startDate,
          next_generation_date: startDate,
          is_active: true,
          expense_type: loanData.expense_type || null,
          paid_by: loanData.paid_by || null,
          paid_to: loanData.paid_to || null,
        },
      ];

      const { error: recurringError } = await supabase
        .from("recurring_expenses")
        .insert(templates);

      if (recurringError) throw recurringError;

      return loan;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["loans"] });
      queryClient.invalidateQueries({ queryKey: ["recurring-expenses"] });
      toast.success("Loan created with recurring expense templates");
    },
    onError: (error) => {
      toast.error("Failed to create loan: " + error.message);
    },
  });

  const updateLoan = useMutation({
    mutationFn: async ({ id, ...data }: Partial<LoanFormData> & { id: string }) => {
      // 1. Update the loan itself
      const { data: loan, error } = await supabase
        .from("loans")
        .update(data)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;

      // 2. Fetch the full updated loan for recalculation
      const lenderName = data.lender_name ?? loan.lender_name;
      const interestRate = data.interest_rate ?? loan.interest_rate;
      const originalAmount = data.original_amount ?? loan.original_amount;
      const monthlyPayment = data.monthly_payment ?? loan.monthly_payment;
      const vehicleId = data.vehicle_id !== undefined ? data.vehicle_id : loan.vehicle_id;
      const expenseType = data.expense_type !== undefined ? data.expense_type : loan.expense_type;
      const paidBy = data.paid_by !== undefined ? data.paid_by : loan.paid_by;
      const paidTo = data.paid_to !== undefined ? data.paid_to : loan.paid_to;

      // 3. Recalculate interest/principal split
      const monthlyRate = interestRate / 100 / 12;
      const interestAmount = Math.round(originalAmount * monthlyRate * 100) / 100;
      const principalAmount = Math.round((monthlyPayment - interestAmount) * 100) / 100;

      // 4. Update the Interest recurring expense
      const { error: intErr } = await supabase
        .from("recurring_expenses")
        .update({
          amount: interestAmount,
          description: `${lenderName} — Interest`,
          vehicle_id: vehicleId || null,
          expense_type: expenseType || null,
          paid_by: paidBy || null,
          paid_to: paidTo || null,
        })
        .eq("loan_id", id)
        .eq("loan_payment_type", "Interest");

      if (intErr) throw intErr;

      // 5. Update the Principal Paydown recurring expense
      const { error: prinErr } = await supabase
        .from("recurring_expenses")
        .update({
          amount: principalAmount,
          description: `${lenderName} — Principal`,
          vehicle_id: vehicleId || null,
          expense_type: expenseType || null,
          paid_by: paidBy || null,
          paid_to: paidTo || null,
        })
        .eq("loan_id", id)
        .eq("loan_payment_type", "Principal Paydown");

      if (prinErr) throw prinErr;

      return loan;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["loans"] });
      queryClient.invalidateQueries({ queryKey: ["recurring-expenses"] });
      toast.success("Loan and recurring expenses updated");
    },
    onError: (error) => {
      toast.error("Failed to update loan: " + error.message);
    },
  });

  const deleteLoan = useMutation({
    mutationFn: async (id: string) => {
      // recurring_expenses with this loan_id will cascade delete
      const { error } = await supabase.from("loans").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["loans"] });
      queryClient.invalidateQueries({ queryKey: ["recurring-expenses"] });
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      toast.success("Loan and associated recurring expenses deleted");
    },
    onError: (error) => {
      toast.error("Failed to delete loan: " + error.message);
    },
  });

  const addLoanWithBackfill = useMutation({
    mutationFn: async ({
      loanData,
      backfillDates,
      interestAmount,
      principalAmount,
    }: {
      loanData: LoanFormData;
      backfillDates: string[];
      interestAmount: number;
      principalAmount: number;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      if (!company?.id) throw new Error("No company found");

      // 1. Create the loan
      const { data: loan, error } = await supabase
        .from("loans")
        .insert({
          ...loanData,
          company_id: company.id,
        })
        .select()
        .single();

      if (error) throw error;

      // 2. Determine next_generation_date (first future date after backfill)
      // We use getScheduledDates to find it, but the caller already computed it;
      // derive from backfillDates: if backfill exists, advance one month past last backfill date
      const startDate = loanData.origination_date;
      const { getScheduledDates } = await import("@/lib/dateUtils");
      const { nextGenerationDate } = getScheduledDates(startDate, "monthly");

      // 3. Spawn two recurring_expenses templates
      const templates = [
        {
          company_id: company.id,
          user_id: user.id,
          vehicle_id: loanData.vehicle_id || null,
          loan_id: loan.id,
          amount: interestAmount,
          loan_payment_type: "Interest",
          description: `${loanData.lender_name} — Interest`,
          frequency: "monthly",
          start_date: startDate,
          next_generation_date: nextGenerationDate,
          is_active: true,
          expense_type: loanData.expense_type || null,
          paid_by: loanData.paid_by || null,
          paid_to: loanData.paid_to || null,
        },
        {
          company_id: company.id,
          user_id: user.id,
          vehicle_id: loanData.vehicle_id || null,
          loan_id: loan.id,
          amount: principalAmount,
          loan_payment_type: "Principal Paydown",
          description: `${loanData.lender_name} — Principal`,
          frequency: "monthly",
          start_date: startDate,
          next_generation_date: nextGenerationDate,
          is_active: true,
          expense_type: loanData.expense_type || null,
          paid_by: loanData.paid_by || null,
          paid_to: loanData.paid_to || null,
        },
      ];

      const { error: recurringError } = await supabase
        .from("recurring_expenses")
        .insert(templates);

      if (recurringError) throw recurringError;

      // 4. Backfill expense records for past dates
      if (backfillDates.length > 0) {
        const expenseRows = backfillDates.flatMap((date) => [
          {
            amount: interestAmount,
            expense_name: `${loanData.lender_name} — Interest`,
            expense_date: date,
            expense_type: loanData.expense_type || null,
            vehicle_id: loanData.vehicle_id || null,
            loan_id: loan.id,
            loan_payment_type: "Interest",
            paid_by: loanData.paid_by || null,
            paid_to: loanData.paid_to || null,
            company_id: company.id,
            user_id: user.id,
          },
          {
            amount: principalAmount,
            expense_name: `${loanData.lender_name} — Principal`,
            expense_date: date,
            expense_type: loanData.expense_type || null,
            vehicle_id: loanData.vehicle_id || null,
            loan_id: loan.id,
            loan_payment_type: "Principal Paydown",
            paid_by: loanData.paid_by || null,
            paid_to: loanData.paid_to || null,
            company_id: company.id,
            user_id: user.id,
          },
        ]);

        const { error: expenseError } = await supabase
          .from("expenses")
          .insert(expenseRows);

        if (expenseError) throw expenseError;
      }

      return loan;
    },
    onSuccess: (_, { backfillDates }) => {
      queryClient.invalidateQueries({ queryKey: ["loans"] });
      queryClient.invalidateQueries({ queryKey: ["recurring-expenses"] });
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      const msg = backfillDates.length > 0
        ? `Loan created with ${backfillDates.length} months of backfilled expenses`
        : "Loan created with recurring expense templates";
      toast.success(msg);
    },
    onError: (error) => {
      toast.error("Failed to create loan: " + error.message);
    },
  });

  return { loans, isLoading, addLoan, updateLoan, deleteLoan, addLoanWithBackfill };
};
